import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/infrastructure/api/client";
import {
  ApiError,
  type PackingItemResponseDto,
  type PackingProgressDto,
  type CreatePackingItemDto,
  type UpdatePackingItemDto,
  type BulkCreatePackingItemsDto,
  type SuggestedPackingItemDto,
} from "@tabipla/shared";

// Query keys
export const packingKeys = {
  all: ["packing"] as const,
  lists: () => [...packingKeys.all, "list"] as const,
  list: (tripId: string) => [...packingKeys.lists(), tripId] as const,
  progress: (tripId: string) => [...packingKeys.all, "progress", tripId] as const,
};

// Get packing items for a trip
export function usePackingItems(tripId: string) {
  return useQuery({
    queryKey: packingKeys.list(tripId),
    queryFn: async () => {
      const response = await apiClient.get<PackingItemResponseDto[]>(
        `/api/trips/${tripId}/packing`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch packing items");
      }
      return response.data;
    },
    enabled: !!tripId,
  });
}

// Get packing progress
export function usePackingProgress(tripId: string) {
  return useQuery({
    queryKey: packingKeys.progress(tripId),
    queryFn: async () => {
      const response = await apiClient.get<PackingProgressDto>(
        `/api/trips/${tripId}/packing/progress`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch packing progress");
      }
      return response.data;
    },
    enabled: !!tripId,
  });
}

// Create packing item
export function useCreatePackingItem(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePackingItemDto) => {
      const response = await apiClient.post<PackingItemResponseDto>(
        `/api/trips/${tripId}/packing`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create packing item");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: packingKeys.list(tripId) });
      queryClient.invalidateQueries({ queryKey: packingKeys.progress(tripId) });
    },
  });
}

// Bulk create packing items
export function useBulkCreatePackingItems(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkCreatePackingItemsDto) => {
      const response = await apiClient.post<PackingItemResponseDto[]>(
        `/api/trips/${tripId}/packing/bulk`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create packing items");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: packingKeys.list(tripId) });
      queryClient.invalidateQueries({ queryKey: packingKeys.progress(tripId) });
    },
  });
}

// Update packing item
export function useUpdatePackingItem(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, data }: { itemId: string; data: UpdatePackingItemDto }) => {
      const response = await apiClient.put<PackingItemResponseDto>(
        `/api/trips/${tripId}/packing/${itemId}`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update packing item");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: packingKeys.list(tripId) });
      queryClient.invalidateQueries({ queryKey: packingKeys.progress(tripId) });
    },
  });
}

// Toggle packing item checked status
export function useTogglePackingItem(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemId: string) => {
      const response = await apiClient.patch<PackingItemResponseDto>(
        `/api/trips/${tripId}/packing/${itemId}/toggle`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to toggle packing item");
      }
      return response.data;
    },
    onMutate: async (itemId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: packingKeys.list(tripId) });

      // Snapshot the previous value
      const previousItems = queryClient.getQueryData<PackingItemResponseDto[]>(
        packingKeys.list(tripId)
      );

      // Optimistically update to the new value
      if (previousItems) {
        queryClient.setQueryData<PackingItemResponseDto[]>(
          packingKeys.list(tripId),
          previousItems.map((item) =>
            item.id === itemId ? { ...item, isChecked: !item.isChecked } : item
          )
        );
      }

      return { previousItems };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousItems) {
        queryClient.setQueryData(packingKeys.list(tripId), context.previousItems);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: packingKeys.list(tripId) });
      queryClient.invalidateQueries({ queryKey: packingKeys.progress(tripId) });
    },
  });
}

// Set packing item priority (1-3 stars)
export function useSetPriority(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, priority }: { itemId: string; priority: number | null }) => {
      const response = await apiClient.patch<PackingItemResponseDto>(
        `/api/trips/${tripId}/packing/${itemId}/priority`,
        { priority }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to set priority");
      }
      return response.data;
    },
    onMutate: async ({ itemId, priority }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: packingKeys.list(tripId) });

      // Snapshot the previous value
      const previousItems = queryClient.getQueryData<PackingItemResponseDto[]>(
        packingKeys.list(tripId)
      );

      // Optimistically update to the new value
      if (previousItems) {
        queryClient.setQueryData<PackingItemResponseDto[]>(
          packingKeys.list(tripId),
          previousItems.map((item) => (item.id === itemId ? { ...item, priority } : item))
        );
      }

      return { previousItems };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousItems) {
        queryClient.setQueryData(packingKeys.list(tripId), context.previousItems);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: packingKeys.list(tripId) });
    },
  });
}

// Delete packing item
export function useDeletePackingItem(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemId: string) => {
      const response = await apiClient.delete(`/api/trips/${tripId}/packing/${itemId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete packing item");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: packingKeys.list(tripId) });
      queryClient.invalidateQueries({ queryKey: packingKeys.progress(tripId) });
    },
  });
}

// Delete all packing items
export function useDeleteAllPackingItems(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete(`/api/trips/${tripId}/packing`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete all packing items");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: packingKeys.list(tripId) });
      queryClient.invalidateQueries({ queryKey: packingKeys.progress(tripId) });
    },
  });
}

// Get AI suggested packing items
export function useAISuggestPackingItems(tripId: string) {
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.get<SuggestedPackingItemDto[]>(
        `/api/trips/${tripId}/packing/suggest`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to get AI suggestions");
      }
      return response.data;
    },
  });
}
